/**
 * Currency Symbol & Decimal Price Formatting Utilities
 * Adheres strictly to Constitution Principle III (Money representation).
 */

import { findCurrencyByCode } from './currencies';

const CURRENCY_SYMBOLS: Record<string, string> = {
  SAR: 'ر.س',
  AED: 'د.إ',
  EGP: 'ج.م',
  KWD: 'د.ك',
  QAR: 'ر.ق',
  BHD: 'د.ب',
  OMR: 'ر.ع',
  JOD: 'د.أ',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  TRY: '₺',
  BRL: 'R$',
  MXN: 'Mex$',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  NZD: 'NZ$',
  SGD: 'S$',
  HKD: 'HK$',
  KRW: '₩',
  ZAR: 'R',
};

/**
 * Returns the standard display symbol for a 3-letter currency code.
 */
export function getCurrencySymbol(currencyCode?: string | null): string {
  if (!currencyCode) return '';
  const upper = currencyCode.trim().toUpperCase();
  if (!upper) return '';
  const info = findCurrencyByCode(upper);
  if (info) return info.symbol;
  return CURRENCY_SYMBOLS[upper] ?? upper;
}

/**
 * Formats a decimal amount and currency code into a human-readable string.
 * e.g. ("348.00", "USD") -> "$348.00", ("5000", "JPY") -> "¥5,000"
 */
export function formatCurrencyPrice(
  amount: string | number | null | undefined,
  currencyCode?: string | null
): string {
  if (amount === null || amount === undefined || amount === '') {
    return '—';
  }

  const num = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ''));
  if (isNaN(num)) {
    return '—';
  }

  const upper = (currencyCode || '').trim().toUpperCase();
  const isZeroDecimal = upper === 'JPY' || upper === 'KRW';

  try {
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isZeroDecimal ? 0 : 2,
      maximumFractionDigits: isZeroDecimal ? 0 : 2,
    }).format(num);

    if (!upper) {
      return formattedNumber;
    }

    const symbol = getCurrencySymbol(upper);

    if (symbol === upper) {
      return `${upper} ${formattedNumber}`;
    }

    // Single-char symbols like $, €, £, ¥ look best snug to the number
    // Multi-char symbols (ر.س, ج.م, CA$, kr, etc.) require a space for clean readability
    const isSingleCharSymbol = symbol.length === 1 && !/[\u0600-\u06FF]/.test(symbol);
    if (isSingleCharSymbol) {
      return `${symbol}${formattedNumber}`;
    }

    return `${symbol} ${formattedNumber}`;
  } catch {
    if (!upper) {
      return num.toFixed(isZeroDecimal ? 0 : 2);
    }
    const symbol = getCurrencySymbol(upper);
    const isSingleCharSymbol = symbol.length === 1 && !/[\u0600-\u06FF]/.test(symbol);
    const space = isSingleCharSymbol ? '' : ' ';
    return `${symbol}${space}${num.toFixed(isZeroDecimal ? 0 : 2)}`;
  }
}
